import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type L from 'leaflet';
import type { Mark, QidUser } from '../../api';
import { getTheme, useThemeMode } from '../../theme';
import { markImg, GREEN_PLACEHOLDER, Empty } from '../../components';
import { NAV_MAIN_H, NAV_TABS_H } from '../../components/TopNav';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ListMap } from './MarkMaps';
import {
  SPECIES_CHIPS, gameTypes, priceLabel, matchesSpecies, placeLine, markPath, markPrices, loadFavs, saveFavs,
} from './markData';

import { JAKT_SUB_TABS, type JaktSubTab } from './marketplace';
export type { JaktSubTab };

/** Titelrad: "Jakt i Sverige" + rubrik, segmenterad kontroll för undersidorna till höger. */
export function JaktHeader({ sub, onSub }: { sub: JaktSubTab; onSub: (s: JaktSubTab) => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const mob = useIsMobile();
  const title = JAKT_SUB_TABS.find(([k]) => k === sub)?.[1] ?? 'Jakt';
  return (
    <div style={{ display: 'flex', flexDirection: mob ? 'column' : 'row', alignItems: mob ? 'stretch' : 'flex-end', justifyContent: 'space-between', gap: mob ? 12 : 20, marginBottom: 16 }}>
      <div>
        <div style={{ fontSize: 13, color: T.textSub, fontWeight: 600 }}>Jakt i Sverige</div>
        <h1 style={{ fontSize: mob ? 26 : 32, fontWeight: 800, letterSpacing: '-0.02em', margin: '2px 0 0', color: T.text }}>{title}</h1>
      </div>
      <div className="hide-scrollbar" role="tablist" style={{ display: 'flex', gap: 6, padding: 4, background: T.surface2, borderRadius: 999, overflowX: 'auto', scrollbarWidth: 'none', alignSelf: mob ? 'stretch' : 'auto' }}>
        {JAKT_SUB_TABS.map(([k, l]) => {
          const on = sub === k;
          return (
            <button key={k} role="tab" aria-selected={on} onClick={() => onSub(k)} style={{ padding: '8px 16px', borderRadius: 999, border: 'none', background: on ? T.surface : 'transparent', color: on ? T.text : T.textSub, boxShadow: on ? '0 1px 3px rgba(0,0,0,.12)' : 'none', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>{l}</button>
          );
        })}
      </div>
    </div>
  );
}

/** Pill-dropdown ("Län ▾"). Menyn ligger fixed så att den inte klipps av den scrollande filterraden på mobil. */
function PillSelect({ label, value, options, onChange, plain }: { label: string; value: string; options: [string, string][]; onChange: (v: string) => void; plain?: boolean }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [open, setOpen] = useState<DOMRect | null>(null);
  const btn = React.useRef<HTMLButtonElement>(null);
  const menu = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => { if (!menu.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) setOpen(null); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(null); btn.current?.focus(); } };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('scroll', close, true); window.removeEventListener('keydown', esc); };
  }, [open]);
  const on = !!value && !plain;
  const cur = options.find(([v]) => v === value)?.[1];
  const style: React.CSSProperties = plain
    ? { background: 'transparent', border: 'none', color: T.text, fontSize: 14, fontWeight: 700, padding: '8px 0', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }
    : { padding: '8px 14px', borderRadius: 999, border: `1px solid ${on ? T.text : T.border}`, background: on ? T.text : T.surface, color: on ? T.bg : T.text, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 };
  const pick = (v: string) => { onChange(v); setOpen(null); };
  const right = open ? Math.max(8, window.innerWidth - open.right) : 0;
  const alignRight = !!open && open.left + 240 > window.innerWidth;
  return (
    <>
      <button ref={btn} type="button" aria-haspopup="listbox" aria-expanded={!!open} aria-label={`${label}${cur ? `: ${cur}` : ''}`} onClick={() => setOpen(o => (o ? null : btn.current!.getBoundingClientRect()))} style={style}>
        {cur ?? label} ▾
      </button>
      {open && (
        <div ref={menu} role="listbox" aria-label={label} style={{ position: 'fixed', top: open.bottom + 6, ...(alignRight ? { right } : { left: open.left }), zIndex: 1200, minWidth: Math.max(200, open.width), maxHeight: 320, overflowY: 'auto', padding: 6, borderRadius: 12, background: T.surface, border: `1px solid ${T.border}`, boxShadow: '0 10px 30px rgba(0,0,0,.14)' }}>
          {!plain && <button type="button" role="option" aria-selected={!value} onClick={() => pick('')} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: 8, border: 'none', background: !value ? T.surface2 : 'transparent', color: T.textSub, fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>Alla</button>}
          {options.map(([v, l]) => (
            <button key={v} type="button" role="option" aria-selected={v === value} onClick={() => pick(v)} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: 8, border: 'none', background: v === value ? T.surface2 : 'transparent', color: T.text, fontSize: 14, fontWeight: v === value ? 700 : 500, fontFamily: 'inherit', cursor: 'pointer' }}>
              {l}{v === value && <span style={{ color: T.accentText }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** Singularform för räknetexten ("1 jaktmark"). */
const NOUN_ONE: Record<string, string> = { jaktmarker: 'jaktmark', jaktklubbar: 'jaktklubb', 'enskilda jakter': 'enskild jakt' };

const YTA: [string, string][] = [['0-200', 'Under 200 ha'], ['200-500', '200–500 ha'], ['500-1000', '500–1 000 ha'], ['1000-', 'Över 1 000 ha']];
const PRIS: [string, string][] = [['dag', 'Dagjakt finns'], ['0-10000', 'Under 10 000 kr'], ['10000-20000', '10 000–20 000 kr'], ['20000-', 'Över 20 000 kr']];
const SORT: [string, string][] = [['relevans', 'Relevans'], ['pris-lag', 'Lägsta pris'], ['pris-hog', 'Högsta pris'], ['yta', 'Störst areal'], ['namn', 'Namn A–Ö']];

const inRange = (n: number | undefined, r: string) => {
  if (n == null) return false;
  const [lo, hi] = r.split('-').map(x => (x === '' ? undefined : Number(x)));
  return (lo == null || n >= lo) && (hi == null || n < hi);
};

function readArt(): string[] {
  const a = new URLSearchParams(window.location.search).get('art');
  return a ? a.split(',').filter(x => (SPECIES_CHIPS as readonly string[]).includes(x)) : [];
}

/** Pris att visa på ett kort; `sort` används för sortering och prisfilter. */
export type TilePrice = { price: string; unit: string; sort: number } | null;

export function MarkTile({ mark, kind, showPrice, fav, onFav, onOpen, onHover, mob, price, sub, href }: {
  mark: Mark; kind: string; showPrice: boolean; fav: boolean; onFav: () => void; onOpen: () => void; onHover?: (h: boolean) => void; mob?: boolean;
  /** Annonsens pris (annars markens). */ price?: TilePrice;
  /** Extra rad under namnet, t.ex. annonsens datum. */ sub?: string;
  href?: string;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [h, setH] = useState(false);
  const gt = gameTypes(mark).slice(0, 3);
  const pl = price !== undefined ? price : priceLabel(mark);
  return (
    <a
      href={href ?? markPath(mark)}
      onClick={e => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; e.preventDefault(); onOpen(); }}
      onMouseEnter={() => { setH(true); onHover?.(true); }} onMouseLeave={() => { setH(false); onHover?.(false); }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12, color: T.text, textDecoration: 'none', minWidth: 0 }}
    >
      <div style={{ position: 'relative', aspectRatio: '4 / 3', borderRadius: 14, overflow: 'hidden', background: T.surface2 }}>
        <img src={markImg(mark)} alt={mark.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform .4s ease', transform: h ? 'scale(1.04)' : 'none' }} onError={e => { (e.target as HTMLImageElement).src = GREEN_PLACEHOLDER; }} />
        <div style={{ position: 'absolute', top: 12, left: 12, right: 56, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {gt.map((g, i) => (
            <span key={g} style={{ padding: '4px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', background: i === 0 ? '#c96a0c' : 'rgba(255,255,255,.94)', color: i === 0 ? '#fff' : '#1c2117' }}>{g}</span>
          ))}
          {mark.isPublished === false && <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 800, background: 'rgba(180,40,40,.9)', color: '#fff' }}>EJ PUBLICERAD</span>}
        </div>
        <button
          type="button" aria-label={fav ? 'Ta bort från sparade' : 'Spara'} aria-pressed={fav}
          onClick={e => { e.preventDefault(); e.stopPropagation(); onFav(); }}
          style={{ position: 'absolute', top: 10, right: 10, width: mob ? 44 : 36, height: mob ? 44 : 36, borderRadius: '50%', background: 'rgba(255,255,255,.92)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: fav ? '#c96a0c' : '#1c2117', cursor: 'pointer', fontFamily: 'inherit' }}
        >{fav ? '♥' : '♡'}</button>
        {mark.areaHa ? <span style={{ position: 'absolute', bottom: 12, left: 12, padding: '4px 10px', borderRadius: 999, background: 'rgba(20,28,16,.78)', color: '#fff', fontSize: 12, fontWeight: 700 }}>{mark.areaHa.toLocaleString('sv-SE')} ha</span> : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '0 2px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, color: T.textSub }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{placeLine(mark)}</span><span style={{ flexShrink: 0 }}>{kind}</span>
        </div>
        <div style={{ fontSize: mob ? 17 : 18, fontWeight: 700, letterSpacing: '-.01em' }}>{mark.name}</div>
        {sub && <div style={{ fontSize: 13, color: T.textSub }}>{sub}</div>}
        <div style={{ fontSize: 15, marginTop: 2 }}>
          {showPrice && pl ? <><b>{pl.price}</b> <span style={{ color: T.textSub }}>{pl.unit}</span></>
            : showPrice ? <span style={{ color: T.textSub }}>Kontakta för pris</span>
            : <span style={{ color: T.textSub }}>Logga in för att se pris</span>}
        </div>
      </div>
    </a>
  );
}

/** Listvyn för annonser (jaktlag, enskilda jakter, jaktklubbar, jaktpaket): filter, sortering, kortgrid och karta bredvid. */
export function MarkList({ marks, loading, user, kind, noun, onOpen, onLogin, emptySub, priceOf, kindOf, subOf, hrefOf }: {
  marks: Mark[]; loading: boolean; user?: QidUser | null; kind: string; noun: string;
  onOpen: (m: Mark) => void; onLogin?: () => void; emptySub: string;
  /** Annonslistor: pris, typ, extrarad och länk per post (posten är annonsens mark med annonsens id/rubrik). */
  priceOf?: (m: Mark) => TilePrice; kindOf?: (m: Mark) => string; subOf?: (m: Mark) => string | undefined; hrefOf?: (m: Mark) => string;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const mob = useIsMobile();
  const showPrice = !!user;

  const [active, setActive] = useState<string[]>(readArt);
  const [lan, setLan] = useState('');
  const [yta, setYta] = useState('');
  const [pris, setPris] = useState('');
  const [sort, setSort] = useState('relevans');
  const [favs, setFavs] = useState<Set<string>>(loadFavs);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [searchOnMove, setSearchOnMove] = useState(true);
  const [bounds, setBounds] = useState<L.LatLngBounds | null>(null);
  const [mobMap, setMobMap] = useState(false);

  // Artchipsen synkas med URL:en (?art=Älg,Rådjur)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (active.length) p.set('art', active.join(',')); else p.delete('art');
    const q = p.toString();
    window.history.replaceState(window.history.state, '', window.location.pathname + (q ? `?${q}` : '') + window.location.hash);
  }, [active]);

  const toggleSpecies = (n: string) => setActive(a => (a.includes(n) ? a.filter(x => x !== n) : [...a, n]));
  const toggleFav = (id: string) => {
    if (!user) { onLogin?.(); return; }
    setFavs(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); saveFavs(n); return n; });
  };

  const counties = useMemo(() => [...new Set(marks.map(m => m.county).filter((c): c is string => !!c))].sort((a, b) => a.localeCompare(b, 'sv')), [marks]);

  const filtered = useMemo(() => {
    let r = marks.filter(m => matchesSpecies(m, active));
    if (lan) r = r.filter(m => m.county === lan);
    if (yta) r = r.filter(m => inRange(m.areaHa, yta));
    if (pris) r = r.filter(m => {
      if (!priceOf) return pris === 'dag' ? !!markPrices(m).day : inRange(markPrices(m).season, pris);
      const p = priceOf(m);
      return !!p && (pris === 'dag' ? p.unit === '/ dag' : p.unit !== '/ dag' && inRange(p.sort, pris));
    });
    const price = (m: Mark) => (priceOf ? priceOf(m) : priceLabel(m))?.sort;
    const by: Record<string, (a: Mark, b: Mark) => number> = {
      relevans: (a, b) => Number(!!b.featured) - Number(!!a.featured),
      'pris-lag': (a, b) => (price(a) ?? Infinity) - (price(b) ?? Infinity),
      'pris-hog': (a, b) => (price(b) ?? -1) - (price(a) ?? -1),
      yta: (a, b) => (b.areaHa ?? 0) - (a.areaHa ?? 0),
      namn: (a, b) => a.name.localeCompare(b.name, 'sv'),
    };
    return [...r].sort(by[sort] ?? by.relevans);
  }, [marks, active, lan, yta, pris, sort, priceOf]);

  // Kartans urval: marker utan koordinater visas alltid i listan
  const shown = useMemo(() => (bounds ? filtered.filter(m => m.mapLat == null || m.mapLng == null || bounds.contains([m.mapLat, m.mapLng])) : filtered), [filtered, bounds]);

  const pinText = useCallback((m: Mark) => {
    const pl = showPrice ? (priceOf ? priceOf(m) : priceLabel(m)) : null;
    if (pl) return pl.price.replace('från ', '');
    return m.areaHa ? `${m.areaHa.toLocaleString('sv-SE')} ha` : m.name;
  }, [showPrice, priceOf]);

  const filterCount = active.length + [lan, yta, pris].filter(Boolean).length;
  const pill = (on: boolean): React.CSSProperties => ({ padding: '8px 14px', borderRadius: 999, fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'inherit', border: `1px solid ${on ? T.text : T.border}`, background: on ? T.text : T.surface, color: on ? T.bg : T.text });
  const stickyTop = NAV_MAIN_H + NAV_TABS_H + 16;

  const map = (
    <ListMap
      marks={filtered} pinText={pinText} activeId={hoverId} onPick={onOpen}
      searchOnMove={searchOnMove} onToggleSearchOnMove={() => setSearchOnMove(s => !s)} onBounds={setBounds}
      height={mob ? `calc(100dvh - ${NAV_MAIN_H + 150}px - var(--qv-tabs-h, ${NAV_TABS_H}px) - var(--qv-bottom-nav, 0px))` : `calc(100vh - ${stickyTop + 24}px)`}
    />
  );

  return (
    <div>
      {/* Filterrad */}
      <div className="hide-scrollbar" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: mob ? 'nowrap' : 'wrap', overflowX: mob ? 'auto' : 'visible', scrollbarWidth: 'none', paddingBottom: 18, borderBottom: `1px solid ${T.border}`, margin: mob ? '0 -16px' : 0, paddingLeft: mob ? 16 : 0, paddingRight: mob ? 16 : 0 }}>
        {SPECIES_CHIPS.map(n => <button key={n} type="button" aria-pressed={active.includes(n)} onClick={() => toggleSpecies(n)} style={pill(active.includes(n))}>{n}</button>)}
        <span style={{ width: 1, height: 24, background: T.border, margin: '0 6px', flexShrink: 0 }} />
        <PillSelect label="Län" value={lan} onChange={setLan} options={counties.map(c => [c, c])} />
        <PillSelect label="Yta" value={yta} onChange={setYta} options={YTA} />
        {showPrice && <PillSelect label="Pris" value={pris} onChange={setPris} options={PRIS} />}
        {filterCount > 0 && <button type="button" onClick={() => { setActive([]); setLan(''); setYta(''); setPris(''); }} style={{ background: 'none', border: 'none', color: T.accentText, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 }}>Rensa</button>}
        {!mob && <span style={{ marginLeft: 'auto', fontSize: 14, color: T.textSub, display: 'inline-flex', alignItems: 'center', gap: 4 }}>Sortera: <PillSelect plain label="Sortera" value={sort} onChange={setSort} options={SORT} /></span>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : 'minmax(0,1fr) minmax(340px, 470px)', gap: 24, paddingTop: 18 }}>
        {(!mob || !mobMap) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, color: T.textSub }}>
              <span><b style={{ color: T.text }}>{shown.length} {shown.length === 1 ? (NOUN_ONE[noun] ?? noun) : noun}</b> · {lan ? `i ${lan}` : bounds && shown.length < filtered.length ? 'i kartans område' : 'i hela Sverige'}</span>
              {mob && <PillSelect plain label="Sortera" value={sort} onChange={setSort} options={SORT} />}
            </div>
            {loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '22px 18px' }}>
                {[1, 2, 3, 4].map(i => <div key={i} style={{ aspectRatio: '4 / 3', borderRadius: 14, background: T.surface2, animation: 'pulse 1.5s infinite' }} />)}
              </div>
            ) : shown.length === 0 ? (
              <Empty emoji="" title={`Inga ${noun}`} sub={filterCount || bounds ? 'Inga träffar med valda filter — prova att ta bort ett filter eller flytta kartan' : emptySub} />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: mob ? 22 : '22px 18px' }}>
                {shown.map(m => (
                  <MarkTile key={m.id} mark={m} kind={kindOf ? kindOf(m) : kind} showPrice={showPrice} fav={favs.has(m.id)} onFav={() => toggleFav(m.id)} onOpen={() => onOpen(m)} onHover={hv => setHoverId(hv ? m.id : null)} mob={mob}
                    price={priceOf ? priceOf(m) : undefined} sub={subOf?.(m)} href={hrefOf?.(m)} />
                ))}
              </div>
            )}
          </div>
        )}
        {!mob && <div style={{ position: 'sticky', top: stickyTop, alignSelf: 'start' }}>{map}</div>}
        {mob && mobMap && map}
      </div>

      {mob && (
        <button type="button" onClick={() => setMobMap(v => !v)} style={{ position: 'fixed', bottom: 'max(calc(26px + env(safe-area-inset-bottom)), calc(var(--qv-bottom-nav, 0px) + 10px))', left: '50%', transform: 'translateX(-50%)', zIndex: 700, padding: '13px 22px', borderRadius: 999, background: T.text, color: T.bg, border: 'none', fontSize: 14, fontWeight: 800, fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(0,0,0,.2)', cursor: 'pointer' }}>
          {mobMap ? 'Lista ☰' : 'Karta ◎'}
        </button>
      )}
    </div>
  );
}
